import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { sanitizeDate } from '@/core/constants/times'
import { useTableParams } from '@/core/hooks/table-params'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { PaginationParams } from '@/core/services/map'
import { getVendors, IVendor } from '@/core/services/vendors'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'
import { MessageBox } from '@/home/components/messages/MessageBox'
import { Pagination } from '@/home/components/Pagination'
import { CreateVendorModal } from '@/home/components/vendors/CreateVendorModal'
import { Trash, User } from 'lucide-react'

export type TableSearchParams = Record<string, string | null | undefined>

export const VendorsPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
    searchParams,
  } = useTableParams()

  const navigate = useNavigate()
  const { hasEditPermission } = useCheckPermission({ page: 'VENDORS' })

  const [showCreateVendorModal, setShowCreateVendorModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentVendor, setCurrentVendor] = useState<IVendor>()
  const [vendors, setVendors] = useState<IVendor[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const searchInput = useRef(null)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const { editionId, festivalName } = useParams()

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data: vendorResponse } = await getVendors({
        festivalEditionId: editionId as string,
        page,
        size,
        sortBy,
        sortDirection,
      })

      setVendors(vendorResponse)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortDirection,
    sortBy,
    setPage,
    setSize,
    setTotalElemenets,
    editionId,
    festivalName,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      setLoading(true)
      try {
        const params: PaginationParams & { festivalEditionId: string } = {
          page: 0,
          size,
          sortBy,
          sortDirection,
          festivalEditionId: editionId as string,
        }

        const { data: res, status } = await getVendors(params)
        if (status === 200) {
          setVendors(res)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setLoading(false)
      }
    },
    [page, size],
  )

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(vendors.map((vendor) => vendor.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row: IVendor) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (row: IVendor) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            navigate(`/vendors/${festivalName}/${editionId}/${row.id}`)
          }}
        >
          {row.name}
        </a>
      ),
    },
    {
      title: 'Products',
      accessor: 'products',
      render: (row: IVendor) => row.products.toString(),
    },
    {
      title: 'Users',
      accessor: 'users',
      render: (row: IVendor) => row.users.toString(),
    },
    {
      title: 'Locations',
      accessor: 'locations',
      render: (row: IVendor) => row.locations.toString(),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setEditMode(true)
            setCurrentVendor(row)
            setShowCreateVendorModal(true)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Vendor Entity"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setCurrentVendor(undefined)
                  setShowCreateVendorModal(true)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Vendor Entity" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          handleSortBy={handleSortBy}
          handleSearch={(col, query) => {
            handleSearchParams(col, query)
          }}
          searchParams={searchParams.current}
          isCheck={isCheck}
          columns={columns}
          data={vendors}
          rowReorder
          onRowReorder={(next) => setVendors(next)}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreateVendorModal
        open={showCreateVendorModal}
        onOpenChange={setShowCreateVendorModal}
        vendor={
          currentVendor as IVendor & {
            locations: { id: string; name: string }[]
          }
        }
        editMode={editMode}
        editionId={editionId as string}
        onSuccess={getData}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Vendor'}
        deleteFunc={() => {}}
        deletedItem={currentVendor?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
